import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { MatMenu } from "@angular/material/menu";
import { Router } from "@angular/router";
import { Proposal } from "app/entities/proposal";
import { ProposalService } from "app/main/pages/proposals/proposals.service";
import { AlertService } from "app/services/alert.service";
import { GtagService } from "app/services/gtag.service";
import { AuthService } from "app/services/auth.service";
import { buildProposalShareMessage, buildWhatsappUrl } from "app/helpers/proposal-share";
import { trackProposalSend } from "app/helpers/proposal-funnel";
import { copyTextWhenReady } from "app/helpers/clipboard";
import { openPendingWindow } from "app/helpers/pending-window";
import { ProposalEventService } from "app/services/proposal.service";
import { SubscriptionRestrictionService } from "app/services/subscription-restriction.service";
import { environment } from "environments/environment";
import { clientPreviewUrl } from "app/helpers/proposal-share";
import { ConfirmActionDialogComponent } from "../../confirm-action-dialog/confirm-action-dialog.component";
import { PreviewProposalDialogComponent } from "../preview-proposal-dialog/preview-proposal-dialog.component";
import { ProposalLogsDialogComponent } from "../proposal-logs-dialog/proposal-logs-dialog.component";
import { Store } from "@ngrx/store";
import { loadProposalFirstSteps } from "app/state/account/account.actions";
import { ProposalApproveDialogComponent } from "../proposal-approve-dialog/proposal-approve-dialog.component";
import { ProposalRefuseDialogComponent } from "../proposal-refuse-dialog/proposal-refuse-dialog.component";
import { ProposalConfirmDateDialogComponent } from "../proposal-confirm-date-dialog/proposal-confirm-date-dialog.component";
import { MatSnackBar } from "@angular/material/snack-bar";

/** Tempo do aviso com "Desfazer" (a API aceita desfazer por 60 s) */
const UNDO_SNACKBAR_MS = 8000;
import { oaDialogConfig } from "app/helpers/oa-dialog";
import { QuotesService } from "../../quote-list/quotes.service";
import { PreviewQuoteComponent } from "../../preview-quote/preview-quote.component";
import { ProposalStatusAction, rowAfterAction } from "app/helpers/proposal-list";
import { describePlan } from "app/helpers/payment-plan";

export interface ProposalStatusActionEvent {
    action: ProposalStatusAction;
    proposal: Proposal;
    /** Campos da linha da lista que mudam com a ação */
    patch: Record<string, any>;
}

@Component({
    selector: "app-proposal-actions",
    templateUrl: "./proposal-actions.component.html",
    styleUrls: ["./proposal-actions.component.scss"],
    exportAs: "menuInOtherComponent",
})
export class ProposalActionsComponent implements OnInit {
    @Input() proposal!: Proposal;
    @Input() editProposal = true;
    @ViewChild(MatMenu, { static: true }) moreMenu!: MatMenu;
    @Output("onProposalChanged") onProposalChanged = new EventEmitter<Proposal>()
    @Output("onProposaDeleted") onProposalDeleted = new EventEmitter<any>()
    /** ORCA-1965: aprovar, reprovar, faturar ou registrar pagamento concluído */
    @Output() onStatusAction = new EventEmitter<ProposalStatusActionEvent>()
    /** ORCA-1965: só as formas de envio (botão "Enviar" da linha dos orçamentos criados) */
    @ViewChild('sendMenu', { static: true }) sendMenu!: MatMenu;

    env = environment

    constructor(
        public _matDialog: MatDialog,
        private _proposalsService: ProposalService,
        private _quotesService: QuotesService,
        private _alertService: AlertService,
        private _router: Router,
        private _proposalEventService: ProposalEventService,
        private _subscriptionRestrictionService: SubscriptionRestrictionService,
        private _store: Store,
        private _gtag: GtagService,
        private _authService: AuthService,
        private _snackBar: MatSnackBar,
    ) { }

    ngOnInit(): void { }

    /** ORCA-1945: "Visualizar como cliente" usa um link próprio (não avisa a empresa nem aprova) */
    get clientPreviewUrl(): string | null {
        return clientPreviewUrl(this.proposal?.tokenLink);
    }

    viewCotacao(): void {
        const cotacaoId = this.proposal?.cotacaoId;
        if (!cotacaoId) {
            return;
        }

        this._quotesService.find(cotacaoId).subscribe(
            (quote) => {
                this._matDialog.open(PreviewQuoteComponent, {
                    panelClass: ['form-dialog', 'big-radius-dialog', 'quote-preview-dialog'],
                    width: '700px',
                    maxWidth: '95vw',
                    data: {
                        quote,
                    },
                });
            },
            (err) => {
                this._alertService.error(err);
            }
        );
    }

    edit() {
        this._router.navigate(["propostas", this.proposal.token]);
    }

    duplicateProposal() {
        if (!this._subscriptionRestrictionService.checkFeatureLimitAndShowDialog("propostas")) {
            this.trackActionEvent('proposal_duplicated', 'Orçamento duplicado');

            this._router.navigate(["propostas", "copy"], {
                queryParams: {
                    proposal_token: this.proposal.token
                },
            });
        }
    }

    openPDF() {
        var featureLimited = this._subscriptionRestrictionService.checkFeatureLimitAndShowDialog("pdf-propostas")
        if (!featureLimited) {
            trackProposalSend(this._gtag, this.proposal, 'pdf', 'proposal_actions');
            const timestamp = new Date().getTime()
            var url = this.env.baseUrlHotsite + '/proposta/' + this.proposal.tokenLink + '.pdf?v=' + timestamp
            window.open(url, '_blank')
        }
    }

    openActionDialog(action: ProposalStatusAction) {
        // ORCA-1932: aprovar pede a data e, no formato novo, a forma de pagamento.
        // ORCA-1966: reprovar pede o motivo (opcional); faturar e pagar, só a confirmação da data.
        // No celular os popups abrem como folha inferior.
        let dialogRef: MatDialogRef<any>;
        if (action === 'aprovar') {
            dialogRef = this._matDialog.open(ProposalApproveDialogComponent, oaDialogConfig('sheet', {
                width: '560px',
                data: { proposal: this.proposal },
            }));
        } else if (action === 'reprovar') {
            dialogRef = this._matDialog.open(ProposalRefuseDialogComponent, oaDialogConfig('sheet', {
                width: '480px',
                data: { proposal: this.proposal },
            }));
        } else {
            dialogRef = this._matDialog.open(ProposalConfirmDateDialogComponent, oaDialogConfig('sheet', {
                width: '480px',
                data: { action, proposal: this.proposal },
            }));
        }

        dialogRef.afterClosed().subscribe(result => {
            if (!result) return;

            const request = {
                aprovar: () => this._proposalsService.aprove(this.proposal.id!, result),
                faturar: () => this._proposalsService.faturar(this.proposal.id!, result),
                pagar: () => this._proposalsService.pagar(this.proposal.id!, result),
                reprovar: () => this._proposalsService.reprove(this.proposal.id!, result),
            }[action];

            const tracking = {
                aprovar: ['proposal_approved', 'Orçamento aprovado'],
                faturar: ['proposal_bill', 'Orçamento faturado'],
                pagar: ['proposal_pay', 'Pagamento confirmado'],
                reprovar: ['proposal_reprove', 'Orçamento reprovado'],
            }[action];

            request().subscribe((data) => {
                this.trackActionEvent(tracking[0], tracking[1], {
                    action_date: result?.date,
                    reason: result?.reason_type || result?.reason,
                });
                this._proposalEventService.initOnNext();
                this._store.dispatch(loadProposalFirstSteps())

                // ORCA-1965: a lista atualiza a linha na hora com o novo status e a situação
                const chosen = data?.chosenPayment as any;
                this.onStatusAction.emit({
                    action,
                    proposal: data,
                    patch: rowAfterAction(action, result?.date, chosen?.method ? describePlan(chosen) : null),
                });

                this.offerUndo(action, data?.undoToken);
            }, (err) => {
                this._alertService.error(err);
            });
        });
    }

    /**
     * ORCA-1966: aviso com "Desfazer" por alguns segundos depois da ação. Desfazer volta o
     * orçamento ao estado anterior (sem notificações) e a lista recarrega.
     */
    private offerUndo(action: ProposalStatusAction, undoToken: string | null | undefined): void {
        const done = {
            aprovar: 'aprovado',
            reprovar: 'reprovado',
            faturar: 'faturado',
            pagar: 'marcado como pago',
        }[action];
        const message = `Orçamento #${this.proposal.proposalNumber} ${done}.`;

        if (!undoToken) {
            this._snackBar.open(message, 'OK', { duration: 4000 });
            return;
        }

        const proposalId = this.proposal.id!;
        const snack = this._snackBar.open(message, 'Desfazer', { duration: UNDO_SNACKBAR_MS, panelClass: ['oa-snackbar-undo'] });

        snack.onAction().subscribe(() => {
            this._proposalsService.undo(proposalId, undoToken).subscribe(
                () => {
                    this.trackActionEvent('proposal_action_undone', 'Ação desfeita pela lista', { action });
                    this._snackBar.open('Ação desfeita.', 'OK', { duration: 3000 });
                    this._proposalEventService.initOnNext();
                    this.onProposalChanged.emit(this.proposal);
                },
                (err) => this._alertService.error(err)
            );
        });
    }

    sendWhatsapp(source, telefone: string) {
        // A aba abre ainda no clique, senão o Safari bloqueia (ORCA-1925)
        const whatsappWindow = openPendingWindow();

        this._proposalsService.copyLink(this.proposal.id, source).subscribe(
            (res) => {
                this.trackActionEvent('proposal_sent_whatsapp', 'Orçamento enviado para o WhatsApp', {
                    channel: 'whatsapp',
                    contact_phone: telefone,
                    source
                });
                trackProposalSend(this._gtag, this.proposal, 'whatsapp', 'proposal_actions');

                let link = res.data.link_token;

                if (this.proposal.status! <= 2) {
                    this.proposal.status = 2;
                    this.proposal.statusClass = "blue-800";
                    this.proposal.statusName = "Enviado";
                }
                this.onProposalChanged.emit(this.proposal)

                this.onProposalChanged.emit(this.proposal)


                const urlWhatsapp = buildWhatsappUrl(telefone, this.getMessageWithLinkProposal(link));
                this._store.dispatch(loadProposalFirstSteps())
                whatsappWindow.navigate(urlWhatsapp);
            },
            (error) => {
                whatsappWindow.close();
                this._alertService.error(error);
            }
        );
    }

    sendEmail(contact: any = null, contactEmail = null) {
        if (this.proposal?.client?.email == null) {
            return;
        }

        const trackingDetails: Record<string, any> = {
            channel: 'email',
            source: 'proposal_actions',
        };

        if (contact && !contactEmail) {
            trackingDetails['contact_id'] = contact.id;
            contact = {
                contact_id: contact.id,
            };
        }
        if (contactEmail) {
            trackingDetails['contact_email'] = contactEmail;
            contact = {
                contact_email: contactEmail,
            };
        }

        this._proposalsService.sendEmail(this.proposal.id!, contact).subscribe(
            (data) => {
                this.trackActionEvent('proposal_sent_email', 'Orçamento enviado por e-mail', trackingDetails);
                trackProposalSend(this._gtag, this.proposal, 'email', 'proposal_actions');
                this._alertService.success("Email enviado com sucesso.");
                this._proposalEventService.initOnNext();
                this.onProposalChanged.emit(data)
                this._store.dispatch(loadProposalFirstSteps())
                // this.dataSource?.load({});
            },
            (err) => {
                this._alertService.error(err);
            }
        );
    }

    preview() {
        this.trackActionEvent('proposal_preview', 'Pré-visualização do orçamento');
        let dialog = PreviewProposalDialogComponent.open(this._matDialog, {
            id: this.proposal?.id,
            token: this.proposal?.tokenLink,
        });
        dialog.afterClosed().subscribe((result) => { });
    }

    confirmDeleteProposal() {
        let dialog = this._matDialog.open(ConfirmActionDialogComponent, {
            panelClass: ["form-dialog"],
            data: {
                title: "Excluir orçamento",
                message: `Tem certeza que deseja excluir o orçamento #${this.proposal.proposalNumber} - ${this.proposal.shortDescription}?`,
                yesText: "Excluir",
                noText: "Cancelar",
                confirmVariant: "danger",
            },
        });
        dialog.afterClosed().subscribe((result) => {
            if (result === true) {
                this.deleteProposal();
            } else {
            }
        });
    }

    deleteProposal() {
        this._proposalsService.destroy(this.proposal.id!).subscribe(
            (_) => {
                this.trackActionEvent('proposal_delete', 'Orçamento excluído', {
                    deleted_at: new Date().toISOString()
                });
                this._proposalEventService.initOnNext();
                this.onProposalChanged.emit(this.proposal)
                this.onProposalDeleted.emit({})
                // this.dataSource?.load({});
            },
            (err) => {
                this._alertService.error(err);
            }
        );
    }

    copy(source) {
        const request = this._proposalsService.copyLink(this.proposal.id, source).toPromise();
        // A cópia começa ainda no clique, senão o Safari não copia (ORCA-1925)
        const copied = copyTextWhenReady(request.then((res) => this.getMessageWithLinkProposal(res.data.link_token)));

        request.then(
            (res) => {
                if (this.proposal.status! <= 2) {
                    this.proposal.status = 2;
                    this.proposal.statusClass = "blue-800";
                    this.proposal.statusName = "Enviado";
                }
                this.onProposalChanged.emit(this.proposal)

                this.trackActionEvent('proposal_link_copied', 'Link do orçamento copiado', {
                    source
                });
                trackProposalSend(this._gtag, this.proposal, 'link', 'proposal_actions');

                this._proposalEventService.initOnNext();

                copied.then((ok) => ok
                    ? this._alertService.success("Link copiado")
                    : this._alertService.error("Não foi possível copiar o link."));

                this._store.dispatch(loadProposalFirstSteps())
            },
            (error) => {
                this._alertService.error(error);
            }
        );
    }

    showLogsProposals() {
        this.trackActionEvent('proposal_show_logs', 'Visualização de logs do orçamento');

        this._matDialog.open(ProposalLogsDialogComponent, {
            panelClass: ["form-dialog", "large-form-dialog"],
            data: {
                token: this.proposal?.token,
            },
        });
    }

    getMessageWithLinkProposal(link) {
        const account = this._authService.currentAccount;
        return buildProposalShareMessage(this.proposal as any, link, account?.legalName || account?.name);
    }

    private trackActionEvent(eventName: string, eventLabel: string, extra: Record<string, any> = {}): void {
        const payload: Record<string, any> = {
            event_category: 'proposal',
            event_label: eventLabel,
            proposal_id: this.proposal?.id,
            client_id: this.proposal?.clientId,
            proposal_status: this.proposal?.status,
            total_gross: this.proposal?.totalGross,
            total_liquid: this.proposal?.totalLiquid,
            ...extra,
        };

        Object.keys(payload).forEach((key) => {
            if (payload[key] === undefined || payload[key] === null) {
                delete payload[key];
            }
        });

        this._gtag.event(eventName, payload);
    }
}
